'use client';

import { useState } from 'react';
import Link from 'next/link';
import { brand } from '@/lib/brand';
import { Logo } from '@/components/Logo';
import { getAlternateEvergreenLanguages, getUniversitySlugForId } from '@/lib/i18n';
import { uiFb } from '@/lib/i18n-helpers';
import { getUniversityHubsByCountry } from '@/lib/university-hub';
import { getUniversityHubSlugForCountry } from '@/lib/slug-university-hub';
import { getCities, getEventsForCity } from '@/lib/data-source';
import { getCitySlugForId } from '@/lib/slug-city';
import { DESTINATIONS, MONTH_NAMES } from '@/lib/country-month-pages';
import { MONTHS } from '@/lib/constants';
import type { CountryCode } from '@/lib/types';
import type { TopPage } from '@/lib/related-pages';
import { Flag } from '@/components/ui/Flag';
import { SubscribeForm } from '@/components/newsletter/SubscribeForm';

// Side-effect import: register evergreen provider for cross-calendar slug resolution
import '@/lib/cross-calendar-pages';

/**
 * Resolve an evergreen page slug for a specific language, falling back to EN.
 * entityId format: 'festival:th', 'long-weekend:ID', 'best-time:th', etc.
 */
function evergreenSlug(entityId: string, lang: string): string {
  const alts = getAlternateEvergreenLanguages(entityId);
  return alts[lang] || alts['en'] || '';
}

/** Build city links for the footer — top 8 by event count */
function getCityLinks(lang: string): { label: string; href: string; code: CountryCode }[] {
  return getCities()
    .map(city => {
      const slug = getCitySlugForId(city.id, lang);
      if (!slug) return null;
      return {
        label: city.name,
        href: `/${slug}`,
        code: city.country as CountryCode,
        eventCount: getEventsForCity(city.id).length,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null)
    .sort((a, b) => b.eventCount - a.eventCount)
    .slice(0, 8);
}

/** Get country-month destination links for a given 1-indexed month */
function getMonthLinks(month: number, lang: string): { label: string; href: string }[] {
  const monthName = MONTH_NAMES[month - 1]; // e.g. 'january'
  return DESTINATIONS.map(dest => {
    const entityId = `country-month:${dest.slugPrefix}:${monthName}`;
    const slug = evergreenSlug(entityId, lang);
    return {
      label: dest.displayName,
      href: slug ? `/${slug}` : '/',
    };
  }).filter(link => link.href !== '/');
}

/** Build month dropdown options: "This Month", "Next Month", then remaining months */
function getMonthOptions(): { value: number; label: string }[] {
  const now = new Date();
  const thisMonth = now.getMonth() + 1; // 1-indexed
  const nextMonth = thisMonth % 12 + 1;

  const options: { value: number; label: string }[] = [
    { value: thisMonth, label: `This Month (${MONTHS[thisMonth - 1]})` },
    { value: nextMonth, label: `Next Month (${MONTHS[nextMonth - 1]})` },
  ];

  // Add remaining 10 months in calendar order after nextMonth
  for (let i = 1; i <= 10; i++) {
    const m = (nextMonth % 12) + i; // wraps 1-12
    const month = ((m - 1) % 12) + 1;
    if (month !== thisMonth && month !== nextMonth) {
      options.push({ value: month, label: MONTHS[month - 1] });
    }
  }

  return options;
}

function getFooterHubs(lang: string) {
  return {
    [uiFb('ui.footer_popular_cities', 'Popular Destinations', lang)]: getCityLinks(lang),
  };
}

/**
 * Reference/utility evergreen hubs (vacation atlas, semester dates, school
 * holidays, admission calendars). The footer is their only site-wide entry
 * point — without these links they're reachable only by deep drilling and were
 * orphaned in the page-graph audit. Lang-aware via evergreenSlug, so a page in
 * `de` links to the `de` variant (e.g. schulferien-deutschland). (almanak-220z.6)
 */
function getGuideLinks(lang: string): { label: string; href: string }[] {
  const specs: { entityId: string; label: string }[] = [
    { entityId: 'vacation-atlas', label: uiFb('ui.footer_vacation_atlas', 'Vacation Days by Country', lang) },
    { entityId: 'semester-worldwide', label: uiFb('ui.footer_semester_worldwide', 'University Semester Dates Worldwide', lang) },
    { entityId: 'school-holidays-gb', label: uiFb('ui.footer_school_holidays_uk', 'UK School Holidays', lang) },
    { entityId: 'school-holidays-de', label: uiFb('ui.footer_school_holidays_de', 'Germany School Holidays', lang) },
    { entityId: 'school-holidays-fr', label: uiFb('ui.footer_school_holidays_fr', 'France School Holidays', lang) },
    { entityId: 'school-holidays-ae', label: uiFb('ui.footer_school_holidays_ae', 'UAE School Holidays', lang) },
    { entityId: 'admission-calendar:ptn', label: uiFb('ui.footer_admission_calendar', 'Indonesia University Admissions', lang) },
    { entityId: 'graduate-admission-calendar:ptn', label: uiFb('ui.footer_graduate_admission', 'Indonesia Graduate Admissions', lang) },
  ];
  return specs
    .map(s => {
      const slug = evergreenSlug(s.entityId, lang);
      return slug ? { label: s.label, href: `/${slug}` } : null;
    })
    .filter((x): x is { label: string; href: string } => x !== null);
}

const WORLDWIDE_HUB_LINKS: { slug: string; label: string }[] = [
  { slug: 'public-holidays', label: 'Public Holidays Around the World' },
  { slug: 'national-days', label: 'National Days & Independence Days' },
  { slug: 'observances', label: 'Global Observances & Awareness Days' },
  { slug: 'cultural-festivals', label: 'Cultural Festivals Worldwide' },
  { slug: 'local-events', label: 'Local Events & Regional Celebrations' },
];

/** Colored dot for popular pages — uses design token vars for theme consistency */
function pageTypeDotColor(href: string): string {
  if (href.includes('university') || href.includes('academic')) return 'var(--cat-purple)';
  if (href.includes('festival')) return 'var(--semantic-warning)';
  if (href.includes('long-weekend')) return 'var(--cat-sky)';
  if (href.includes('best-time')) return 'var(--semantic-success)';
  if (href.includes('nature')) return 'var(--cat-green)';
  if (href.includes('islamic') || href.includes('hindu') || href.includes('christian') || href.includes('lunar')) return 'var(--cat-rose)';
  if (href.match(/-\d{4}-calendar$/)) return 'var(--semantic-info)';
  return 'var(--cat-gray)';
}

/** Badge label + colored class (mirrors countdown pill styling from WorldEventsTicker) */
function pageTypeBadge(href: string): { label: string; className: string } | null {
  if (href.includes('university') || href.includes('academic'))
    return { label: 'Academic', className: 'bg-[#8b5cf6]/12 text-[#8b5cf6] font-bold' };
  if (href.includes('festival'))
    return { label: 'Festival', className: 'bg-[#f59e0b]/12 text-[#f59e0b] font-bold' };
  if (href.includes('long-weekend'))
    return { label: 'Travel', className: 'bg-[#06b6d4]/12 text-[#06b6d4] font-bold' };
  if (href.includes('best-time'))
    return { label: 'Travel', className: 'bg-[#10b981]/12 text-[#10b981] font-bold' };
  if (href.includes('nature'))
    return { label: 'Nature', className: 'bg-[#22c55e]/12 text-[#22c55e] font-bold' };
  if (href.includes('islamic') || href.includes('hindu') || href.includes('christian') || href.includes('lunar'))
    return { label: 'Religion', className: 'bg-[#ec4899]/12 text-[#ec4899] font-bold' };
  if (href.match(/-\d{4}-calendar$/))
    return { label: 'Calendar', className: 'bg-[#3b82f6]/12 text-[#3b82f6] font-bold' };
  return null;
}

interface LandingFooterProps {
  topPages?: TopPage[];
  lang?: string;
}

export function LandingFooter({ topPages, lang = 'en' }: LandingFooterProps) {
  const footerHubs = getFooterHubs(lang);
  const guideLinks = getGuideLinks(lang);
  const uniHubData = getUniversityHubsByCountry();
  const monthOptions = getMonthOptions();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[1].value); // default: next month
  const monthLinks = getMonthLinks(selectedMonth, lang);

  return (
    <footer className="border-t border-border-subtle px-4 py-8 md:py-10 lg:px-16 xl:px-20">
      <div className="mx-auto max-w-6xl">
        {/* Hub links — 4 domain-aligned columns */}
        <nav aria-label="Browse calendars" className="mb-8 grid grid-cols-1 gap-8 text-left md:grid-cols-[1fr_1.2fr_1fr_1.2fr] md:gap-6">
          {/* Worldwide — hub links with keyword-rich title attrs */}
          <div>
            <p className="pb-2 border-b border-border-subtle/50 mb-3 text-caption font-semibold uppercase tracking-wider text-muted">
              {uiFb('ui.footer_worldwide', 'Worldwide Calendars', lang)}
            </p>
            <ul className="space-y-0.5">
              {WORLDWIDE_HUB_LINKS.map(({ slug, label }) => (
                <li key={slug}>
                  <a
                    href={`/${slug}-worldwide-calendar`}
                    className="text-label text-text-secondary transition-colors hover:text-brand md:text-xs"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* What's Happening In — interactive month picker */}
          <div>
            <p className="pb-2 border-b border-border-subtle/50 mb-3 text-caption font-semibold uppercase tracking-wider text-muted">
              {uiFb('ui.footer_whats_happening', 'Explore by Month', lang)}
            </p>
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(Number(e.target.value))}
              aria-label={uiFb('ui.footer_select_month', 'Select month', lang)}
              className="mb-2 w-full rounded border border-border-subtle bg-transparent px-1.5 py-0.5 text-label text-text-secondary md:text-xs"
            >
              {monthOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ul className="flex flex-wrap gap-x-3 gap-y-1">
              {monthLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-label text-text-secondary transition-colors hover:text-brand md:text-xs"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {Object.entries(footerHubs).map(([heading, links]) => (
            <div key={heading}>
              <p className="pb-2 border-b border-border-subtle/50 mb-3 text-caption font-semibold uppercase tracking-wider text-muted">
                {heading}
              </p>
              <ul className="space-y-1">
                {links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="inline-flex items-center gap-1 text-label text-text-secondary transition-colors hover:text-brand md:text-xs"
                    >
                      {'code' in link && <Flag code={(link as { code: CountryCode }).code} size={14} />}
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Academic Calendar — country dropdowns with university links */}
          <div>
            <p className="pb-2 border-b border-border-subtle/50 mb-3 text-caption font-semibold uppercase tracking-wider text-muted">
              {uiFb('ui.footer_academic', 'Academic Calendars', lang)}
            </p>
            <div className="space-y-1">
              {[...uniHubData.entries()].map(([code, data]) => {
                const hubSlug = getUniversityHubSlugForCountry(code, lang);
                return (
                <details key={code} className="group">
                  <summary className="flex cursor-pointer list-none items-center gap-1 text-label text-text-secondary transition-colors hover:text-brand md:text-xs [&::-webkit-details-marker]:hidden">
                    <Flag code={code} size={14} />
                    <a href={`/${hubSlug}`} className="hover:text-brand">{data.name}</a>
                    <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="ml-auto transition-transform group-open:rotate-180">
                      <path d="M2.5 4 L5 6.5 L7.5 4" />
                    </svg>
                  </summary>
                  <ul className="ml-4 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                    {data.universities.map(uni => {
                      const uniSlug = getUniversitySlugForId(uni.id, lang);
                      return (
                        <li key={uni.id}>
                          <a
                            href={uniSlug ? `/${uniSlug}` : `/${hubSlug}`}
                            className="text-caption text-text-secondary/70 transition-colors hover:text-brand"
                          >
                            {uni.abbrev}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </details>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Guides & references — evergreen utility hubs (lang-aware). Their only
            site-wide entry point; without this they were orphaned. (almanak-220z.6) */}
        {guideLinks.length > 0 && (
          <nav aria-label={uiFb('ui.footer_guides', 'Guides & References', lang)} className="mb-8 border-t border-border-subtle pt-4">
            <p className="mb-2 text-caption font-semibold uppercase tracking-wider text-muted">
              {uiFb('ui.footer_guides', 'Guides & References', lang)}
            </p>
            <ul className="flex flex-wrap gap-x-4 gap-y-1">
              {guideLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-label text-text-secondary transition-colors hover:text-brand md:text-xs"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Popular pages — ticker-style horizontal scroll (Tier 2) */}
        {topPages && topPages.length > 0 && (
          <nav aria-label="Popular pages" className="ticker-hover overflow-hidden border-t border-border-subtle py-4 text-center">
            <p className="mb-1.5 text-caption font-semibold uppercase tracking-wider text-muted">
              {uiFb('ui.popular_pages', 'Popular Pages', lang)}
            </p>
            <div
              className="animate-flag-scroll flex items-center gap-5 py-0.5"
              style={{ width: 'max-content' }}
            >
              {[...topPages, ...topPages].map((page, i) => {
                const badge = pageTypeBadge(page.href);
                const label = page.label.length > 40 ? page.label.slice(0, 37) + '\u2026' : page.label;
                return (
                  <a
                    key={`${page.href}-${i}`}
                    href={page.href}
                    className="hover:bg-surface-hover/50 rounded px-1.5 py-0.5 transition-colors"
                  >
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <span
                        className="h-1.5 w-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: pageTypeDotColor(page.href) }}
                        aria-hidden="true"
                      />
                      {page.countryCode && (
                        <span className="flex items-center gap-px shrink-0">
                          <Flag code={page.countryCode} size={14} />
                        </span>
                      )}
                      <span className="text-label font-semibold text-text-primary">{label}</span>
                      {badge && (
                        <span className={`inline-flex items-center rounded px-1 py-px text-caption uppercase leading-none ${badge.className}`}>
                          {badge.label}
                        </span>
                      )}
                    </span>
                  </a>
                );
              })}
            </div>
          </nav>
        )}

        {/* Subscribe form */}
        <div className="border-t border-border-subtle py-5">
          <div className="mx-auto flex max-w-xl flex-col items-center gap-2 text-center sm:flex-row sm:text-left">
            <div className="shrink-0">
              <p className="text-xs font-semibold text-text-primary">
                {uiFb('ui.subscribe.heading', 'Stay Updated', lang)}
              </p>
              <p className="text-caption text-muted">
                {uiFb('ui.subscribe.subtext', 'Get notified about upcoming holidays & events.', lang)}
              </p>
            </div>
            <div className="w-full flex-1">
              <SubscribeForm lang={lang} source="footer" />
            </div>
          </div>
        </div>

        {/* Trust links */}
        <div className="border-t border-border-subtle py-3 text-center">
          <nav className="flex items-center justify-center gap-4 text-xs text-text-secondary" aria-label="Trust links">
            <Link href="/about" className="hover:text-text-primary hover:underline">{uiFb('ui.footer.about', 'About', lang)}</Link>
            <span className="text-border-subtle" aria-hidden="true">&middot;</span>
            <Link href="/licenses" className="hover:text-text-primary hover:underline">{uiFb('ui.footer.licenses', 'Licenses', lang)}</Link>
          </nav>
        </div>

        {/* Logo + copyright (Tier 3) */}
        <div className="border-t border-border-subtle pt-6 text-center">
          <Link href="/" aria-label={uiFb('ui.nav.home', 'Home', lang)}>
            <Logo size="footer" className="justify-center" />
          </Link>
          <p className="mt-0.5 text-caption uppercase tracking-widest text-muted md:text-label">
            {brand.tagline}
          </p>
          <p className="mt-4 text-caption text-muted">
            &copy; {new Date().getFullYear()} {brand.copyright} — All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
